import { useMemo, useState } from 'react';
import { T, useIsDesktop, Bone } from '../reportTokens';
import SectionEmpty from '../SectionEmpty';
import SectionError from '../SectionError';
import { ReportSectionExportButton } from '../components/ReportExportButton';
import { getCurrencySymbol, formatMoneyForExport } from '../../../utils/currency';
import type { OrderLogItem } from '../../../interface/clickHouseInterface';

// CANC-1 — Cancelled Orders drill-in.
//
// One shared component for BOTH Snapshot and Sales (no per-surface variants) and for both
// breakpoints — desktop renders the two-panel shell side by side with a table log, mobile
// stacks the same two panels with card rows. It mirrors the Voids drill shell (Reasons
// breakdown + Log with Export); the one divergence is that the log's columns are
// customer-oriented — no ITEMS, no STAFF, since staff is never populated for cancellations.
//
// Rows come from /sales/live/v2/order-log with `type=cancel`, fetched by the parent screen
// and passed in, so the KPI count and this log always agree.

const LOG_PAGE_SIZE_DESKTOP = 10;
const LOG_PAGE_SIZE_MOBILE = 10;
const REASONS_PAGE_SIZE = 10;

export const NO_REASON = 'No Reason';

const fmtMoney = (v: number) =>
    `${getCurrencySymbol()}${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtCount = (v: number) => Number(v || 0).toLocaleString('en-US');

/** The endpoint returns "Walk-in" itself; this only covers a missing/blank value. */
const displayCustomer = (o: OrderLogItem) => o.customerName || 'Walk-in';

/**
 * The API sends the cancellation reason as `reason`. It is an empty string until the POS
 * captures one, so a blank (or whitespace-only) value buckets under "No Reason", same as Voids.
 */
const displayReason = (o: OrderLogItem) => o.reason?.trim() || NO_REASON;

/**
 * Phone as shown in the log. Masked to the last 4 digits unless the viewer has revealed the
 * column — the same convention (and reveal control) as the Sales Order Log, so this adds no
 * new unmasked exposure. The endpoint sends "—" when there is no customer on the order.
 */
const displayPhone = (o: OrderLogItem, revealed: boolean) => {
    const raw = o.customerPhone;
    if (!raw || raw === '—') return '—';
    const digits = String(raw).replace(/\D/g, '');
    const formatted = digits.length === 10
        ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
        : digits.length === 11 && digits[0] === '1'
            ? `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
            : String(raw);
    return revealed ? formatted : `(XXX) XXX-${formatted.slice(-4)}`;
};

// CANC-2 — always paired with its date, never time alone: "Today" is a live calendar-day
// fetch, but the log can still straddle midnight between when the page loaded and when it's
// read, so a lone time (no date) is ambiguous about which day it belongs to. Showing the date
// unconditionally also matches the Paid Order Log, which never hides its Date column either.
/**
 * `Aug 17, 06:05 AM`. Prefers `updated_time` ("2026-08-29 22:35:57") since it reflects when
 * the cancellation was recorded; falls back to orderDate/orderTime for rows where the backend
 * hasn't backfilled updated_time yet (comes back as "").
 */
const displayTime = (o: OrderLogItem) => {
    const updated = o.updated_time?.trim();
    if (updated) {
        const d = new Date(updated.replace(' ', 'T'));
        if (!isNaN(d.getTime())) {
            const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
            return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
        }
    }
    const time = o.orderTime || '—';
    const raw = o.orderDate;
    if (!raw) return time;
    const d = new Date(`${raw}T00:00:00`);
    if (isNaN(d.getTime())) return `${raw}, ${time}`;
    return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
};

function PagerButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                padding: '4px 10px',
                borderRadius: 6,
                border: `1px solid ${T.cardBorder}`,
                background: disabled ? T.hover : T.card,
                color: disabled ? T.t3 : T.t1,
                fontSize: 11,
                fontWeight: 600,
                cursor: disabled ? 'default' : 'pointer',
            }}
        >
            {label}
        </button>
    );
}

/** Eye / eye-off control that reveals the masked PHONE column — same behavior as the Order Log. */
function PhoneRevealToggle({ revealed, onToggle }: { revealed: boolean; onToggle: () => void }) {
    return (
        <span
            className="material-symbols-rounded"
            role="button"
            title={revealed ? 'Hide phone numbers' : 'Show phone numbers'}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            style={{ fontSize: 15, color: T.t3, cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center' }}
        >
            {revealed ? 'visibility' : 'visibility_off'}
        </span>
    );
}

export interface CancelledOrdersDrillProps {
    /** Surface the drill was opened from — used for the export file name only. */
    surface: 'snapshot' | 'sales';
    /** Cancelled orders for the active period + source (/order-log, type=cancel). */
    orders: OrderLogItem[];
    loading?: boolean;
    failure?: boolean;
    onRetry?: () => void;
    /** Human-readable period, e.g. "Aug 4 – Aug 17, 2026" — shown in the "All outlets" export header. */
    exportDateRangeLabel?: string;
    /**
     * Fetches the cancelled orders for ONE outlet, for the active period + source. The
     * "All outlets" export scope calls `buildExport` once per accessible outlet with that
     * outlet's id; without this the export would repeat the on-screen outlet's rows for every
     * outlet. Supplied by the parent screen, which owns locationId / dates / source.
     */
    fetchOrdersForExport?: (locationId: string) => Promise<OrderLogItem[]>;
}

export default function CancelledOrdersDrill({
    surface,
    orders,
    loading = false,
    failure = false,
    onRetry,
    exportDateRangeLabel,
    fetchOrdersForExport,
}: CancelledOrdersDrillProps) {
    const isDesktop = useIsDesktop();
    const [phoneRevealed, setPhoneRevealed] = useState(false);
    const [logPage, setLogPage] = useState(1);
    const [reasonsPage, setReasonsPage] = useState(1);

    const totalCount = orders.length;
    const totalAmount = useMemo(() => orders.reduce((s, o) => s + (Number(o.amount) || 0), 0), [orders]);

    // Reasons breakdown, descending by count, keyed off each order's `reason`. While the POS
    // leaves that blank this collapses to a single "No Reason" bucket carrying the real count,
    // and splits into real buckets on its own once reasons start being captured.
    const reasons = useMemo(() => {
        const counts = new Map<string, number>();
        orders.forEach((o) => {
            const key = displayReason(o);
            counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        const rows = Array.from(counts.entries())
            .map(([reason, count]) => ({ reason, count }))
            .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
        const max = rows.reduce((m, r) => Math.max(m, r.count), 1);
        return rows.map((r) => ({ ...r, pct: Math.round((r.count / max) * 100) }));
    }, [orders]);

    // A cancellation is reported in the period it falls in, so the log and the count always
    // agree — the sub-header simply restates the rows below it.
    const subHeader = `${fmtCount(totalCount)} cancelled · ${fmtMoney(totalAmount)} total cancelled value`;

    const logPageSize = isDesktop ? LOG_PAGE_SIZE_DESKTOP : LOG_PAGE_SIZE_MOBILE;
    const logTotalPages = Math.max(1, Math.ceil(orders.length / logPageSize));
    // Clamp rather than reset via an effect so a shrinking list can't strand the page out of range.
    const logSafePage = Math.min(logPage, logTotalPages);
    const logRows = orders.slice((logSafePage - 1) * logPageSize, logSafePage * logPageSize);

    const reasonsTotalPages = Math.max(1, Math.ceil(reasons.length / REASONS_PAGE_SIZE));
    const reasonsSafePage = Math.min(reasonsPage, reasonsTotalPages);
    const reasonRows = reasons.slice((reasonsSafePage - 1) * REASONS_PAGE_SIZE, reasonsSafePage * REASONS_PAGE_SIZE);

    const exportButton = orders.length > 0 ? (
        <ReportSectionExportButton
            variant={isDesktop ? 'desktop' : 'mobile'}
            dateRangeLabel={exportDateRangeLabel}
            title="Cancellation Log"
            filename={`${surface}-cancellation-log`}
            buildExport={async (_format, opts) => {
                // `opts.locationId` is set only for an "All outlets" export, and names an outlet
                // OTHER than the one on screen — fetch that outlet's rows. Without it, the
                // in-memory list is already the whole current-outlet result (single un-paginated
                // fetch), so no refetch is needed.
                const exportRows = opts?.locationId && fetchOrdersForExport
                    ? await fetchOrdersForExport(opts.locationId)
                    : orders;
                return {
                // The export carries customer PII, so it is gated to the same roles that may
                // export the Order Log — it goes through the shared export control, which is
                // where that gating lives.
                headers: ['Order #', 'Date & Time', 'Customer', 'Phone', 'Reason', 'Amount'],
                rows: exportRows.map((o) => [
                    `#${o.orderNo}`,
                    displayTime(o),
                    displayCustomer(o),
                    // Always exported unmasked, independent of the on-screen reveal toggle —
                    // same convention as the Paid Order Log export (orderLogToExportTable).
                    displayPhone(o, true),
                    displayReason(o),
                    formatMoneyForExport(o.amount),
                ]),
                };
            }}
        />
    ) : null;

    // ── Cancellation Reasons ────────────────────────────────────────────────
    const reasonsPanel = (
        <div className="rpt-card" style={{ flex: 1, minWidth: 0, padding: isDesktop ? 22 : 14, marginBottom: isDesktop ? 0 : 12 }}>
            <div style={{ fontSize: isDesktop ? 13 : 12, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Cancellation Reasons</div>
            <div style={{ fontSize: 11, color: T.t3, marginBottom: 14 }}>Why orders were cancelled</div>
            {loading ? (
                <div>
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                <Bone h={12} r={3} style={{ width: `${45 + i * 8}%` }} />
                                <Bone w={24} h={12} r={3} />
                            </div>
                            <Bone h={6} r={3} />
                        </div>
                    ))}
                </div>
            ) : failure ? (
                <SectionError kpiName="cancellation reasons" onRetry={onRetry} size={isDesktop ? 'medium' : 'small'} minHeight={isDesktop ? 160 : 80} />
            ) : reasonRows.length ? (
                <>
                    {reasonRows.map((r) => (
                        <div key={r.reason} style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
                                <span style={{ fontSize: isDesktop ? 12.5 : 12, color: T.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason}</span>
                                <span style={{ fontSize: isDesktop ? 12.5 : 12, fontWeight: 600, color: T.t1, flexShrink: 0 }}>{r.count}x</span>
                            </div>
                            <div style={{ height: 6, background: T.hover, borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${r.pct}%`, background: T.orange, borderRadius: 3 }} />
                            </div>
                        </div>
                    ))}
                    {reasonsTotalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
                            <span style={{ fontSize: 11, color: T.t3 }}>
                                {(reasonsSafePage - 1) * REASONS_PAGE_SIZE + 1}–{Math.min(reasonsSafePage * REASONS_PAGE_SIZE, reasons.length)} of {reasons.length}
                            </span>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <PagerButton label="← Prev" disabled={reasonsSafePage === 1} onClick={() => setReasonsPage(Math.max(1, reasonsSafePage - 1))} />
                                <span style={{ fontSize: 11, color: T.t2, padding: '4px 6px' }}>{reasonsSafePage} / {reasonsTotalPages}</span>
                                <PagerButton label="Next →" disabled={reasonsSafePage >= reasonsTotalPages} onClick={() => setReasonsPage(Math.min(reasonsTotalPages, reasonsSafePage + 1))} />
                            </div>
                        </div>
                    )}
                </>
            ) : (
                // Zero state: the panel is never hidden, it just reports that nothing was cancelled.
                <SectionEmpty kpiName="cancellation reasons" message="No cancelled orders in this period" size={isDesktop ? 'medium' : 'small'} minHeight={isDesktop ? 160 : 80} />
            )}
        </div>
    );

    // ── Cancellation Log ────────────────────────────────────────────────────
    const logHeader = (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: isDesktop ? 13 : 12, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Cancellation Log</div>
                <div style={{ fontSize: 11, color: T.t3 }}>Cancelled order details</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                {/* Mobile has no column header to hang the reveal control off, so it sits beside Export. */}
                {!isDesktop && orders.length > 0 && <PhoneRevealToggle revealed={phoneRevealed} onToggle={() => setPhoneRevealed((v) => !v)} />}
                {exportButton}
            </div>
        </div>
    );

    const LOG_GRID = '1.1fr 1.1fr 1.2fr 1.2fr 1.6fr 0.9fr';
    const cellClip = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 } as const;

    const desktopLogTable = (
        <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 640 }}>
                <div style={{ display: 'grid', gridTemplateColumns: LOG_GRID, gap: 8, padding: '6px 0', borderBottom: `1px solid ${T.divider}`, marginBottom: 4 }}>
                    {['Order #', 'Date & Time', 'Customer', 'Phone', 'Reason', 'Amount'].map((h, i) => (
                        <div
                            key={h}
                            style={{
                                fontSize: 10, fontWeight: 600, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.5,
                                display: 'flex', alignItems: 'center', gap: 4,
                                justifyContent: i === 5 ? 'flex-end' : 'flex-start',
                            }}
                        >
                            {h}
                            {h === 'Phone' && <PhoneRevealToggle revealed={phoneRevealed} onToggle={() => setPhoneRevealed((v) => !v)} />}
                        </div>
                    ))}
                </div>
                {logRows.map((o, i) => (
                    <div
                        key={o.orderId || `${o.orderNo}-${i}`}
                        style={{
                            display: 'grid', gridTemplateColumns: LOG_GRID, gap: 8, padding: '10px 0', alignItems: 'center',
                            borderBottom: i < logRows.length - 1 ? `1px solid ${T.divider}` : 'none',
                        }}
                    >
                        <div title={`#${o.orderNo}`} style={{ fontSize: 12, fontWeight: 600, color: T.t1, ...cellClip }}>#{o.orderNo}</div>
                        <div style={{ fontSize: 12, color: T.t2, whiteSpace: 'nowrap' }}>{displayTime(o)}</div>
                        <div title={displayCustomer(o)} style={{ fontSize: 12, fontWeight: 500, color: T.t1, ...cellClip }}>{displayCustomer(o)}</div>
                        <div style={{ fontSize: 12, color: T.t2, whiteSpace: 'nowrap' }}>{displayPhone(o, phoneRevealed)}</div>
                        <div title={displayReason(o)} style={{ fontSize: 12, color: T.t2, ...cellClip }}>{displayReason(o)}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.orange, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtMoney(o.amount)}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const mobileLogList = (
        <div>
            {logRows.map((o, i) => (
                <div
                    key={o.orderId || `${o.orderNo}-${i}`}
                    style={{ padding: '12px 0', borderBottom: i < logRows.length - 1 ? `1px solid ${T.divider}` : 'none' }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.t1, ...cellClip }}>#{o.orderNo}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: T.orange, flexShrink: 0 }}>{fmtMoney(o.amount)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: T.t1, marginTop: 3, ...cellClip }}>
                        {displayCustomer(o)} · {displayPhone(o, phoneRevealed)}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginTop: 3 }}>
                        <span style={{ fontSize: 11, color: T.t3, flexShrink: 0 }}>{displayTime(o)}</span>
                        <span style={{ fontSize: 11, color: T.t3, textAlign: 'right', ...cellClip }}>{displayReason(o)}</span>
                    </div>
                </div>
            ))}
        </div>
    );

    const logPanel = (
        <div className="rpt-card" style={{ flex: isDesktop ? 2 : undefined, minWidth: 0, overflow: 'hidden', padding: isDesktop ? 22 : 14 }}>
            {logHeader}
            {loading ? (
                <div>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 0', borderBottom: i < 5 ? `1px solid ${T.divider}` : 'none' }}>
                            <Bone w={60} h={12} r={3} />
                            <Bone h={12} r={3} style={{ flex: 1 }} />
                            <Bone w={50} h={12} r={3} />
                            <Bone w={55} h={12} r={3} />
                        </div>
                    ))}
                </div>
            ) : failure ? (
                <SectionError kpiName="cancellation log" onRetry={onRetry} size={isDesktop ? 'medium' : 'small'} minHeight={isDesktop ? 160 : 80} />
            ) : orders.length ? (
                <>
                    {isDesktop ? desktopLogTable : mobileLogList}
                    {logTotalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
                            <span style={{ fontSize: 11, color: T.t3 }}>
                                {(logSafePage - 1) * logPageSize + 1}–{Math.min(logSafePage * logPageSize, orders.length)} of {orders.length}
                            </span>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <PagerButton label="← Prev" disabled={logSafePage === 1} onClick={() => setLogPage(Math.max(1, logSafePage - 1))} />
                                <span style={{ fontSize: 11, color: T.t2, padding: '4px 6px' }}>{logSafePage} / {logTotalPages}</span>
                                <PagerButton label="Next →" disabled={logSafePage >= logTotalPages} onClick={() => setLogPage(Math.min(logTotalPages, logSafePage + 1))} />
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <SectionEmpty kpiName="cancellation log" message="No cancelled orders in this period" size={isDesktop ? 'medium' : 'small'} minHeight={isDesktop ? 160 : 80} />
            )}
        </div>
    );

    return (
        <div style={{ padding: isDesktop ? 4 : '0 14px 16px' }}>
            <div style={{ fontSize: isDesktop ? 11 : 12, color: T.t3, marginBottom: isDesktop ? 16 : 12 }}>
                {loading ? <Bone w={220} h={12} r={4} /> : subHeader}
            </div>
            {isDesktop ? (
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    {reasonsPanel}
                    {logPanel}
                </div>
            ) : (
                <>
                    {reasonsPanel}
                    {logPanel}
                </>
            )}
        </div>
    );
}
